import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Pagination,
  IconButton,
  PaginationItem,
  Popper,
  ClickAwayListener,
  TableContainer,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import draft from "../../../../assets/images/draft.svg";
import approved from "../../../../assets/images/approved.svg";
import { useClaimDetailsStore } from "../../../../store/useClaimDetailsStore";
import { useEffect, useState } from "react";
import Dropdown from "../../../../components/Dropdown";
import AssignmentPopup from "../../../../components/AssignmentPopup";
import {
  assignMe,
  changeClaimAssignment,
  closeClaim,
  getClaimHistory,
  getUserRoles,
} from "../../../../services/dashboard";
import VerifiedPopup from "../../../../components/VerifiedPopup";
import { useVerifiedStore } from "../../../../store/useVerifiedStore";
import { useAuthStore } from "../../../../store/useAuthStore";
import History from "../common/History";
import Loader from "../../../../components/Loader";
import dayjs from "dayjs";
import { useUserStore } from "../../../../store/useUserStore";
import { useToast } from "../../../../hooks/useToast";
import CloseClaimPopup from "../../../../components/CloseClaimPopup";
import { useClaimTypeStore } from "../../../../store/useClaimTypeStore";
import CheckBoxRoundedIcon from "@mui/icons-material/CheckBoxRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";

const getClaimStatusColor = (status?: string) => {
  switch (status) {
    case "APPROVED":
    case "REVIEWED_APPROVED":
      return "#009252";
    case "DECLINED":
    case "REVIEWED_DECLINED":
      return "#D32F2F";
    case "REVIEWED":
      return "#ED6C02";
    default:
      return "#1976D2";
  }
};

const getClaimStatusLabel = (status?: string) => {
  const labels: Record<string, string> = {
    REVIEWED: "Reviewed",
    REVIEWED_APPROVED: "Reviewed - Approved",
    REVIEWED_DECLINED: "Reviewed - Declined",
    APPROVED: "Authorised",
    DECLINED: "Declined",
  };
  return status ? labels[status] || status.replace(/_/g, " ") : "";
};

const isStatusInRoleQueue = (role?: string, status?: string) => {
  const normalizedStatus = status?.startsWith("REVIEWED_") ? "REVIEWED" : status;
  const statusesByRole: Record<string, string[]> = {
    consultant: ["DRAFT"],
    claim_administrator: ["VETTED"],
    claim_manager: ["NEW"],
    underwriter: ["REVIEWED", "DECLINED", "REJECTED"],
    finance: ["APPROVED"],
  };
  return Boolean(normalizedStatus && statusesByRole[String(role).toLowerCase()]?.includes(normalizedStatus));
};

const ClaimList = ({
  totalClaimCount,
  onSelectClaim,
  currentPage,
  rowsPerPage,
  setCurrentPage,
  resetToClaimList,
  setNewClaimClick,
  onStatusCardClick,
  selectedStatus,
}: any) => {
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState<null | HTMLElement>(
    null,
  );
  const [showAssignment, setShowAssignment] = useState(false);
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [selectedClaim, setSelectedClaim] = useState<any>(null);
  const [userRoles, setUserRoles] = useState<any[]>([]);
  const [popupLabel, setPopupLabel] = useState("Assigned");
  const [showHistory, setShowHistory] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [historyData, setHistoryData] = useState<any>(null);
  const [showCloseClaim, setShowCloseClaim] = useState(false);
  const [closeReason, setCloseReason] = useState("");

  const rawClaims = useClaimDetailsStore(
    (state) => state.filteredClaims || state.claims,
  );
  const claims = rawClaims || [];
  const filteredClaims = useClaimDetailsStore((state) => state.filteredClaims);
  const searchedClaim = useClaimDetailsStore((state) => state.searchedClaim);
  const statusCounts = useClaimDetailsStore((state) => state.statusCounts);
  const showVerified = useVerifiedStore((state) => state.showVerified);
  const setShowVerified = useVerifiedStore((state) => state.setShowVerified);
  const { userId, first_name, last_name } = useAuthStore();
  const role = useUserStore((state) => state.role);
  const isUnderwriter = String(role).toLowerCase() === "underwriter";
  const claimType = useClaimTypeStore((state) => state.claimType);
  const isDeathClaim = claimType === "DEATH";

  const metricCards = isUnderwriter
    ? [
        { key: "REVIEWED", label: "Total Reviewed", value: statusCounts?.REVIEWED || 0, color: "#ED6C02", icon: draft },
        { key: "REVIEWED_APPROVED", label: "Reviewed - Approved", value: statusCounts?.REVIEWED_APPROVED || 0, color: "#009252", icon: approved },
        { key: "REVIEWED_DECLINED", label: "Reviewed - Declined", value: statusCounts?.REVIEWED_DECLINED || 0, color: "#D32F2F", icon: approved },
        { key: "APPROVED", label: "Authorised", value: statusCounts?.APPROVED || 0, color: "#4CAF50", icon: approved },
        { key: "DECLINED", label: "Declined", value: statusCounts?.DECLINED || 0, color: "#1B56EB", icon: approved },
      ]
    : [
        { key: "DRAFT", label: "Total open Draft", value: statusCounts?.DRAFT || 0, color: "#4CAF50", icon: draft },
        { key: "APPROVED", label: "Approved", value: statusCounts?.APPROVED || 0, color: "orange", icon: approved },
        { key: "NEW", label: "New Claims", value: statusCounts?.NEW || 0, color: "#1B56EB", icon: approved },
      ];

  const fullName = `${first_name} ${last_name}`;

  const { showInfoToast } = useToast();

  const paginatedClaims =
    (Array.isArray(searchedClaim) && searchedClaim) ||
    (Array.isArray(filteredClaims) && filteredClaims) ||
    (Array.isArray(claims) && claims) ||
    [];

  const handleOpenDropdown = (
    event: React.MouseEvent<HTMLButtonElement>,
    claim: any,
  ) => {
    setDropdownAnchorEl(event.currentTarget);
    setSelectedClaim(claim);
  };

  const handleCloseDropdown = () => {
    setDropdownAnchorEl(null);
  };

  const DROPDOWN_OPTIONS = {
    notAssigned: ["Assign Me", "Assign Someone", "History", "Close"],
    assignedMe: ["Reassign Someone", "Unassign", "History", "Close"],
    assignedSomeone: [
      "Reassign to me",
      "Reassign Someone",
      "Unassign",
      "History",
      "Close",
    ],
  };

  const getDropdownOptions = () => {
    if (selectedClaim.status === "PAID") {
      return ["History"];
    }
    // if (selectedClaim.is_closable) {
    //   return ["History", "Close"];
    // }

    let options: string[] = [];

    if (selectedClaim.assigned_to === "Unassigned") {
      options = DROPDOWN_OPTIONS.notAssigned;
    } else if (!isStatusInRoleQueue(role, selectedClaim.status)) {
      options = ["History", "Close"];
    } else if (
      selectedClaim.assigned_to_user_id === userId ||
      selectedClaim.assigned_to === fullName
    ) {
      options = DROPDOWN_OPTIONS.assignedMe;
    } else {
      options = DROPDOWN_OPTIONS.assignedSomeone;
    }

    // const isAuthorized =
    //   role === "consultant" || role === "claim_administrator";
    // if (!isAuthorized) {
    //   options = options.filter((option) => option !== "Close");
    // }

    if (role === "underwriter") {
      options = options.filter((option) => option !== "History");
    }
    return options;
  };

  const callUserRoles = async () => {
    setIsLoading(true);
    const res = await getUserRoles(role?.toUpperCase(), userId);
    if (res.status === 200) {
      setUserRoles(res.data);
    } else {
      showInfoToast(res.data.message);
    }
    setIsLoading(false);
  };

  const handleHistoryClick = async (id: string) => {
    setIsLoading(true);
    const res = await getClaimHistory(id);
    if (res.status === 200) {
      setHistoryData(res.data);
      setShowHistory(true);
    } else {
      showInfoToast(res.data.message);
    }
    setIsLoading(false);
  };

  const handleBackFromHistory = () => {
    setShowHistory(false);
  };

  const onOptionClick = (option: string) => {
    setSelectedValue(option);
    if (option === "Assign Me") {
      setShowAssignment(true);
    }
    if (option === "Assign Someone") {
      callUserRoles();
      setShowAssignment(true);
    }
    if (option === "Reassign Someone") {
      callUserRoles();
      setShowAssignment(true);
    }
    if (option === "Reassign to me" || option === "Unassign") {
      setShowAssignment(true);
    }
    if (option === "History") {
      handleHistoryClick(selectedClaim.claim_id);
    }
    if (option === "Close") {
      setShowCloseClaim(true);
    }
  };

  const handleCloseAssignmentPopup = () => {
    setShowAssignment(false);
    setSelectedValue("");
  };

  const onAssignmentHandle = async (selectedUserId?: string | null, assignmentReason?: string) => {
    setIsLoading(true);
    setShowAssignment(false);
    if (!selectedClaim) return;
    const isAssignmentChange = ["Reassign to me", "Reassign Someone", "Unassign"].includes(
      selectedValue || "",
    );
    if (isAssignmentChange) {
      if (!selectedClaim.assigned_to_user_id) {
        showInfoToast("Claim assignment details are outdated. Refresh the claim and try again.");
        resetToClaimList();
        setIsLoading(false);
        return;
      }
      const res = await changeClaimAssignment({
        claim_id: selectedClaim.claim_id,
        requested_by_user_id: userId,
        expected_assigned_to_user_id: selectedClaim.assigned_to_user_id,
        new_assigned_to_user_id:
          selectedValue === "Unassign" ? null : selectedUserId || userId,
        reason: assignmentReason?.trim(),
      });
      if (res?.status === 200) {
        setPopupLabel(selectedValue === "Unassign" ? "Claim Unassigned" : "Claim Reassigned");
        setShowVerified(true);
      } else {
        showInfoToast(res?.data?.message || "Failed to change the claim assignment.");
        if (res?.status === 409) resetToClaimList();
      }
      setIsLoading(false);
      return;
    }
    const userIdToSend = selectedUserId || userId;
    const payload = {
      claim_id: selectedClaim.claim_id,
      assigned_to_user_id: userIdToSend,
      assigned_by_user_id: userId,
      claimant_personal_information: {
        comments: "",
      },
      claims_benefit_schedule: {
        comments: "",
      },
      banking_information: {
        comments: "",
      },
      policy_information: {
        comments: "",
      },
      details_of_deceased: {
        comments: "",
      },
    };
    const res = await assignMe(payload);
    if (res.status === 200) {
      setPopupLabel("Assigned");
      setShowVerified(true);
    } else {
      showInfoToast(res?.data?.message || "Failed to assign the claim.");
      if (res?.status === 409) {
        resetToClaimList();
      }
    }
    setIsLoading(false);
  };

  const handleReasonChange = (value: any) => {
    setCloseReason(value);
  };

  const handleCloseClaim = async () => {
    const payload = {
      claim_id: selectedClaim.claim_id,
      user_id: userId,
      claim_status: "CLOSED",
      status_reason: closeReason,
    };
    const res = await closeClaim(payload);
    if (res.status === 200) {
      setPopupLabel("Claim Closed");
      setShowVerified(true);
      setCloseReason("");
    }
  };

  useEffect(() => {
    if (showVerified) {
      const timer = setTimeout(() => {
        resetToClaimList();
        setShowVerified(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showVerified, resetToClaimList]);

  useEffect(() => {
    setNewClaimClick(false);
  }, [searchedClaim, filteredClaims]);

  if (showHistory) {
    return (
      <Box sx={{ flexGrow: 1, overflowY: "auto" }}>
        <History
          onBack={handleBackFromHistory}
          historyData={historyData}
          isLoading={isLoading}
        />
      </Box>
    );
  }
  return (
    <Box
      overflow="hidden"
      p={{ xs: 1, sm: 2, md: 3 }}
      bgcolor="background.paper"
      height={{ xs: "auto", md: "100vh" }}
      display="flex"
      flexDirection="column"
      width="100%"
      sx={{
        boxSizing: "border-box",
        overflowX: "auto",
      }}
    >
      {isLoading ? <Loader isLoading={isLoading} /> : null}
      <Box flexShrink={0} sx={{ marginTop: { xs: 7, sm: 0 } }}>
        <Typography
          fontSize={{ xs: 18, sm: 22, md: 25 }}
          fontWeight={600}
          color="primary.main"
          textAlign="left"
        >
          {isDeathClaim ? "Death Claim" : "10% Claim"}
        </Typography>

        {/* Metrics Cards */}
        <Box
          mt={2}
          display="grid"
          gridTemplateColumns={{
            xs: "1fr",
            sm: "repeat(2, minmax(0, 1fr))",
            lg: `repeat(${metricCards.length}, minmax(0, 1fr))`,
          }}
          gap={3}
        >
          {metricCards.map((card) => (
            <Paper
              key={card.key}
              onClick={() => onStatusCardClick?.(card.key)}
              sx={{
                p: 2,
                display: "flex",
                alignItems: "center",
                borderBottom: `4px solid ${card.color}`,
                background: "#ffffff",
                minWidth: 0,
                height: { xs: "auto", md: 150 },
                cursor: "pointer",
                transition: "box-shadow 0.2s ease",
                boxShadow: selectedStatus === card.key ? 8 : undefined,
                outline: selectedStatus === card.key ? `2px solid ${card.color}` : undefined,
                "&:hover": { boxShadow: 6 },
              }}
            >
              <Box display="flex" alignItems="center" gap={2} minWidth={0}>
                <img
                  src={card.icon}
                  alt={card.label}
                  style={{ width: 40, height: 40, flexShrink: 0 }}
                />
                <Box minWidth={0}>
                  <Typography variant="h2" fontWeight={600} textAlign="left" fontSize={{ xs: 20, md: 28 }}>
                    {card.value}
                  </Typography>
                  <Typography
                    variant="h6"
                    textAlign="left"
                    fontWeight={600}
                    fontSize={{ xs: 14, md: 16 }}
                    sx={{ overflowWrap: "anywhere" }}
                  >
                    {card.label}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>

      {/* Table Section */}
      <Box
        mt={4}
        flex={1}
        overflow="auto"
        sx={{
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        <Box display="flex" alignItems="center">
          <Typography fontWeight={600}>Recent claims</Typography>
        </Box>

        <Paper sx={{ background: "#ffffff", overflowX: "auto" }} elevation={2}>
          <TableContainer
            sx={{
              maxHeight: 420,
              overflowX: "auto",
            }}
          >
            <Table sx={{ width: "100%" }}>
              <TableHead
                sx={{
                  minWidth: "max-content",
                  "& .MuiTableCell-root": {
                    whiteSpace: "nowrap",
                  },
                }}
              >
                <TableRow>
                  <TableCell>CLAIM</TableCell>
                  <TableCell>POLICY</TableCell>
                  <TableCell>POLICY HOLDER</TableCell>
                  <TableCell>
                    {isDeathClaim ? "CLAIMANT" : "CLAIM AMOUNT"}
                  </TableCell>
                  <TableCell>SLA COMPLIANCE</TableCell>
                  <TableCell>LAST UPDATE</TableCell>
                  <TableCell>ASSIGNED</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedClaims.map((item: any, index: any) => (
                  <TableRow key={item.ice_claim_number}>
                    <TableCell>{item.ice_claim_number}</TableCell>
                    <TableCell>{item.policy_number || "N/A"}</TableCell>
                    <TableCell>
                      <Typography>{item.main_member_name}</Typography>
                      <Typography
                        variant="body2"
                        color="text.disabled"
                      ></Typography>
                    </TableCell>
                    <TableCell>
                      {isDeathClaim ? item.claimant_name : item?.claim_amount}
                    </TableCell>
                    <TableCell sx={{ textAlign: "center" }}>
                      {item.sla_indicator === "ON_TRACK" ||
                      item.sla_indicator === "SLA_MET" ? (
                        <CheckBoxRoundedIcon sx={{ color: "#4CAF50" }} />
                      ) : item.sla_indicator === "BREACHED" ? (
                        <CloseRoundedIcon sx={{ color: "#FF0000" }} />
                      ) : item.sla_indicator === "AT_RISK" ? (
                        <WarningRoundedIcon sx={{ color: "#ffc107" }} />
                      ) : item.sla_indicator === "CLOSED" ? (
                        <BlockRoundedIcon color="error" />
                      ) : null}
                    </TableCell>
                    <TableCell>
                      {item.updated_on
                        ? dayjs(item.updated_on).format("YYYY-MM-DD")
                        : item.created_on
                          ? dayjs(item.created_on).format("YYYY-MM-DD")
                          : ""}
                    </TableCell>
                    <TableCell>{item.assigned_to}</TableCell>
                    <TableCell
                      sx={{
                        color: getClaimStatusColor(item.status),
                        fontWeight: 500,
                        cursor: "pointer",
                      }}
                      onClick={() =>
                        onSelectClaim(item.claim_id, item.reassigned, role)
                      }
                    >
                      {getClaimStatusLabel(item.status)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleOpenDropdown(e, item)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {dropdownAnchorEl && selectedClaim && (
            <Popper
              open={Boolean(dropdownAnchorEl)}
              anchorEl={dropdownAnchorEl}
              placement="bottom-start"
              modifiers={[
                { name: "offset", options: { offset: [-135, 0] } },
                { name: "preventOverflow", options: { boundary: "viewport" } },
                { name: "flip", enabled: true },
              ]}
            >
              <ClickAwayListener onClickAway={handleCloseDropdown}>
                <div>
                  <Dropdown
                    onClose={handleCloseDropdown}
                    options={getDropdownOptions()}
                    onClick={(option: string) => {
                      onOptionClick(option);
                      handleCloseDropdown();
                    }}
                  />
                </div>
              </ClickAwayListener>
            </Popper>
          )}
        </Paper>
        {totalClaimCount > rowsPerPage && (
          <Box display="flex" justifyContent="center" p={2}>
            <Pagination
              count={Math.ceil(totalClaimCount / rowsPerPage)}
              page={currentPage + 1}
              onChange={(_, page) => setCurrentPage(page)}
              siblingCount={0}
              boundaryCount={1}
              variant="outlined"
              shape="rounded"
              color="primary"
              renderItem={(item) => (
                <PaginationItem
                  {...item}
                  components={{
                    next: () => <span style={{ padding: "0 8px" }}>Next</span>,
                  }}
                />
              )}
            />
          </Box>
        )}
      </Box>
      <AssignmentPopup
        open={showAssignment}
        onClose={handleCloseAssignmentPopup}
        selectedValue={selectedValue}
        userRoles={
          selectedValue === "Reassign Someone"
            ? userRoles.filter((user: any) => user.userId !== selectedClaim?.assigned_to_user_id)
            : userRoles
        }
        onAssignmentHandle={onAssignmentHandle}
        requireReason={["Reassign to me", "Reassign Someone", "Unassign"].includes(
          selectedValue || "",
        )}
      />
      <VerifiedPopup
        label={popupLabel}
        open={showVerified}
        onClose={() => setShowVerified(false)}
      />
      <CloseClaimPopup
        open={showCloseClaim}
        onClose={() => setShowCloseClaim(false)}
        onChange={handleReasonChange}
        handleCloseClaim={handleCloseClaim}
        closeReason={closeReason}
      />
    </Box>
  );
};

export default ClaimList;
